import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

const stockPerm = permissionTable([
  [
    'manage_inventory',
    'Required for Stock dashboard, products, purchases, movements, suppliers, and stock reports (hotel package dependent)',
  ],
]);
const roomInvPerm = permissionTable([
  [
    'manage_inventory',
    'Required for Room Inventory dashboard, list, and Manage Inventory grid',
  ],
]);

export const stockGuides = [
  buildGuide({
    id: 'stock-overview',
    title: 'Stock Management Overview',
    moduleId: 'stock',
    summary:
      'Track hotel and restaurant consumables: items, purchases, movements, suppliers, and reports.',
    keywords: [
      'stock',
      'inventory stock',
      'raw materials',
      'purchase',
      'suppliers',
      'consumables',
    ],
    pathHint: 'Hotel → Stock',
    related: [
      'stock-dashboard',
      'stock-items',
      'stock-in',
      'stock-movement',
      'stock-suppliers',
      'stock-reports',
    ],
    videos: [
      video(
        'rfH7qYq6O7w',
        'Stock management software demo',
        'Smart inventory and stock control walkthrough for hotel and restaurant stores.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Stock Management in Roomnexa tracks physical goods your property buys and consumes — linen chemicals, guest amenities, F&B raw materials, beverages, and similar store items. It answers four daily questions: what do we have on hand, what did we buy, what did we issue or adjust, and what is running low.',
        'This module is not the same as Room Inventory. Room Inventory controls sellable rooms and nights. Stock controls consumable quantities and purchase history. New staff often confuse the two names; keep them separate in training.',
        'Stock is organised around a simple loop: create products (raw materials), register suppliers, post Stock In when goods arrive, record Stock Movement when goods leave the store or move between hotel and restaurant pools, then review Stock Reports for low stock and valuation.',
        'Hotel and restaurant can be tracked as separate departments (purchase-for / pool context). Use that split when the property wants kitchen stock and housekeeping store stock reported independently.',
        'Access depends on your role permission and hotel package. If Stock is missing from the menu, ask your admin to check manage_inventory and the subscribed package — do not assume the feature is broken.'
      ),
      callout(
        'note',
        'Two “inventories”',
        'Room Inventory = sellable rooms/nights. Stock = consumable goods. Both exist in Roomnexa and serve different teams.'
      ),
      table(
        ['Area', 'Use it for'],
        [
          ['Dashboard', 'On-hand health, movement trends, hotel vs restaurant pool view'],
          ['Products / Raw materials', 'Item catalogue (name, unit, reorder thresholds)'],
          ['Suppliers', 'Vendor master for purchases'],
          ['Add Stock / Stock In', 'Goods receipt with invoice, tax, and payment details'],
          ['Stock Movement', 'Stock out, transfer, adjustment, and item ledger'],
          ['Reports', 'Summary, current stock, movement, consumption, low stock, valuation'],
        ]
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Agree who owns the store (FO supervisor, HK, or F&B) and who may post adjustments.',
        'Decide whether hotel and restaurant stock are tracked separately.',
        'Collect first supplier invoices and a physical count of key items before go-live.',
        'Train staff that issuing goods without posting a movement creates silent stock loss.',
      ]),
    ],
    steps: [
      heading('Recommended setup order'),
      steps([
        {
          text: 'Open Stock and confirm you can see Dashboard, Products, Suppliers, Add Stock, Movement, and Reports.',
        },
        {
          text: 'Create Products / Raw materials for every item you will track — name, unit, category, and reorder thresholds.',
        },
        {
          text: 'Add Suppliers with contacts and GST/FSSAI details where required.',
        },
        {
          text: 'Post opening balances via Stock In (or adjustments after a physical count) so on-hand quantities are real.',
        },
        {
          text: 'Train store keepers on Stock Out / Transfer / Adjustment and on reading the item ledger.',
        },
        {
          text: 'Run Low Stock and Valuation reports weekly; reconcile valuation to purchase invoices monthly.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Stock Dashboard — KPI tiles and hotel/restaurant pool utilisation',
          'Products / Raw materials — catalogue with active flag and reorder settings',
          'Suppliers — vendor list with add/edit drawers',
          'Add Stock — purchase form with AI invoice extract, line items, GST, payments',
          'Stock Movement — issue out, transfer, adjust, open item ledger',
          'Stock Reports — report type menu, filters, export/print',
        ],
        'Main Stock screens'
      ),
    ],
    workflow: [
      workflow('Purchase to consumption', [
        'Supplier delivery',
        'Count goods',
        'Stock In (invoice + lines)',
        'Store on hand',
        'Stock Out / Transfer',
        'Reports & reorder',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Stock In increases on-hand quantity and contributes to average cost / valuation. Stock Out and consumption decrease on-hand. Transfers move quantity between hotel and restaurant pools without treating the move as a purchase. Adjustments correct differences after physical audit and should always carry a clear reason.',
        'Reports only reflect documents that were posted in Roomnexa. Paper invoices left in a drawer will not appear in valuation or GST-related cost tracking until Stock In is saved.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'AI invoice extract',
        'On Add Stock, use invoice extract to speed entry, then verify every quantity, rate, and tax line before saving.'
      ),
      callout(
        'tip',
        'Units first',
        'Pick the shelf unit you count (kg, L, pcs). Convert supplier case packs into that unit on Stock In — never mix units on the same SKU.'
      ),
      callout(
        'important',
        'Who may adjust',
        'Large positive/negative adjustments should require a supervisor. Adjustments without investigation can hide theft or receiving errors.'
      ),
    ],
    bestPractices: [
      bullets([
        'Run Low Stock every Monday before placing supplier orders.',
        'Post Stock In the same day goods arrive — do not batch a week of invoices.',
        'Keep hotel vs restaurant pools clean if F&B and HK report separately.',
        'File invoice photos/PDFs on Stock In so accounts can audit later.',
        'Do a monthly physical count of high-value items and post adjustments with reasons.',
        'Deactivate obsolete products instead of deleting history.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Issuing kitchen goods without recording Stock Out.',
        'Creating duplicate products (“Sugar”, “sugar”, “Sugar 1”).',
        'Entering billed cases as pieces without converting quantity.',
        'Confusing Stock (goods) with Room Inventory (rooms).',
        'Using adjustments to “fix” stock instead of investigating variance.',
        'Leaving suppliers without phone/GST when accounts needs them later.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Stock menu missing',
            'Package or manage_inventory permission',
            'Ask admin to enable module + role permission',
          ],
          [
            'Negative or impossible stock',
            'Out posted without In, or wrong unit',
            'Recount physically; post adjustment; fix process',
          ],
          [
            'Item missing on Stock In',
            'Product inactive or never created',
            'Create/activate in Products / Raw materials',
          ],
          [
            'Reports empty',
            'No postings in date range',
            'Widen dates; confirm Stock In/Out were saved',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is Stock linked to POS recipes automatically?',
          a: 'Stock movements are explicit in Roomnexa. Follow your property SOP for posting kitchen consumption from F&B sales — do not assume every POS order auto-deducts stock.',
        },
        {
          q: 'Do I need Stock if we only buy amenities occasionally?',
          a: 'Optional by package, but even small properties benefit from Stock In + Low Stock for soap, water, and linen chemicals.',
        },
        {
          q: 'Where do purchase vendors live?',
          a: 'Under Stock → Suppliers. Guest customers are in Customer Hub; they are different masters.',
        },
        {
          q: 'Can housekeepers use Stock?',
          a: 'Yes if their role includes manage_inventory. Many hotels give store keepers Stock Out access and reserve adjustments for supervisors.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-dashboard',
    title: 'Stock Dashboard',
    moduleId: 'stock',
    summary: 'KPIs and trends for hotel and restaurant stock health.',
    keywords: ['stock dashboard', 'stock kpi', 'pool inventory', 'on hand'],
    pathHint: 'Stock → Dashboard',
    related: ['stock-reports', 'stock-overview', 'stock-movement'],
    overview: [
      ...paragraphs(
        'The Stock Dashboard is the manager’s morning view of store health. It summarises on-hand posture, recent movement activity, and hotel versus restaurant pool context so you can see pressure before the kitchen or housekeeping runs out.',
        'Use it as a triage screen, not as a substitute for posting. When a KPI looks wrong, drill into Stock Reports or the item ledger to find the documents behind the number.',
        'After banquets, festivals, or large group arrivals, open the dashboard the next morning. Unusual consumption shows up here before menu 86’s or amenity shortages reach guests.',
        'If KPI tiles are empty or near zero, the property may not have posted Stock In/Out yet, or you may be looking at a newly configured hotel with no opening balances.'
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Know which pool (hotel / restaurant) your property tracks.',
        'Have Low Stock and Current Stock reports bookmarked for drill-down.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Stock → Dashboard.' },
        {
          text: 'Review KPI tiles for on-hand value/quantity signals and recent in/out activity.',
        },
        {
          text: 'Compare hotel and restaurant pool utilisation if both are used at your property.',
        },
        {
          text: 'Note any low-stock pressure, then open Stock Reports → Low Stock for the actionable list.',
        },
        {
          text: 'After large events, check trends again and schedule Stock In or transfers as needed.',
        },
      ]),
    ],
    screens: [
      bullets([
        'KPI / metric tiles for stock health',
        'Hotel and restaurant pool utilisation visuals',
        'Trend / movement context for managers',
        'Tab navigation into Products, Add Stock, Movement, Reports',
      ]),
      table(
        ['Signal', 'What to do next'],
        [
          ['Low on-hand / high utilisation', 'Run Low Stock; place orders or transfer between pools'],
          ['Spike in stock out', 'Review Consumption / Movement reports for waste or over-issue'],
          ['Empty dashboard', 'Post opening Stock In; confirm permissions'],
        ]
      ),
    ],
    workflow: [
      workflow('Morning store check', [
        'Open dashboard',
        'Note lows / spikes',
        'Open Low Stock report',
        'Plan purchase or transfer',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Dashboard figures summarise stock documents already posted for the hotel. They do not invent stock from POS sales or from invoices that were never entered.',
        'Hotel and restaurant pools exist so one property can hold separate balances for the same product when purchase-for / department tracking is used.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'After big events',
        'Check the dashboard the morning after banquets and wedding blocks — consumption spikes are easiest to catch early.'
      ),
      callout(
        'tip',
        'Pair with Low Stock',
        'Dashboard shows health; Low Stock report names the SKUs to reorder.'
      ),
      callout(
        'note',
        'Not a count sheet',
        'Physical counts still happen in the store room. Use adjustments after counting, then refresh the dashboard.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review dashboard daily in peak season; at least twice weekly in low season.',
        'Compare dashboard totals to monthly physical counts for high-value items.',
        'Brief F&B and HK supervisors when restaurant vs hotel pools diverge sharply.',
        'Use the same date habits as Finance (week-to-week) when explaining trends to owners.',
        'Investigate sudden valuation drops before month-end closes.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Ignoring low stock until the menu is 86’d or amenities run out at check-in.',
        'Treating dashboard numbers as a physical count without walking the store.',
        'Mixing hotel and restaurant issues when reading pool utilisation.',
        'Looking only at today and missing a weekend consumption spike.',
        'Assuming empty KPIs mean “all good” when nothing was ever posted.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          ['Empty KPIs', 'No stock documents posted', 'Post Stock In/Out; refresh'],
          ['Odd pool imbalance', 'Transfers not posted', 'Post Transfer movements; recheck'],
          ['Numbers disagree with shelf', 'Missing outs or wrong units', 'Recount; adjustment; fix SOP'],
          ['Cannot open Stock', 'Permission/package', 'Ask admin for manage_inventory'],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Does the dashboard include room availability?',
          a: 'No. Room sellability is under Room Inventory / Manage Inventory. Stock Dashboard is consumables only.',
        },
        {
          q: 'Hotel vs restaurant stock — which is correct?',
          a: 'Both can be correct if your property tracks two pools. Issue and transfer into the pool that will consume the goods.',
        },
        {
          q: 'Can I export the dashboard?',
          a: 'Use Stock Reports for printable/exportable registers (PDF, Excel, CSV, print) when you need owner packs.',
        },
        {
          q: 'Why did value drop overnight?',
          a: 'Usually Stock Out, transfer, or negative adjustment posted late. Open Movement or Adjustment report for the date.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-items',
    title: 'Products & Raw Materials',
    moduleId: 'stock',
    summary: 'Maintain the item catalogue used by purchases and movements.',
    keywords: ['raw materials', 'products', 'stock items', 'sku', 'catalogue'],
    pathHint: 'Stock → Products / Raw materials',
    related: ['stock-in', 'stock-suppliers', 'stock-reports'],
    overview: [
      ...paragraphs(
        'Products & Raw Materials is the master catalogue for every tracked stock item. Create each item once with a clear name, unit of measure, category, and reorder settings before you post purchases or movements.',
        'Cashiers and store keepers cannot receive or issue what does not exist in the catalogue. Setup quality here determines whether Stock In, Movement, and Reports stay clean.',
        'Each item can carry a SKU, minimum stock / reorder guidance, and an active flag. Prefer deactivating seasonal or discontinued items instead of deleting them when history already exists.',
        'Units matter more than almost any other field. If you count rice in kilograms on the shelf, the catalogue unit should be kg — not “bag” — and Stock In quantities should be converted accordingly.',
        'Duplicate catalogue rows (“Sugar” vs “sugar”) break reporting and confuse staff. Search before you add.'
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'List the first 20–50 items you actually count (amenities, cleaning, kitchen staples).',
        'Agree shelf units with kitchen and HK before creating the catalogue.',
        'Decide naming conventions (Brand + Item, or generic names).',
      ]),
    ],
    steps: [
      heading('Create a product'),
      steps([
        { text: 'Open Stock → Products / Raw materials.' },
        { text: 'Search the list to confirm the item does not already exist.' },
        {
          text: 'Click Add and enter name, category, SKU (if used), and unit of measure.',
        },
        {
          text: 'Set minimum stock / reorder guidance so Low Stock reports are meaningful.',
        },
        {
          text: 'Leave Active enabled for items you buy now; save the product.',
        },
        {
          text: 'Post the first Stock In against the new item and verify it appears on Current Stock.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Name', 'Yes', 'Guest/store-facing identity — keep stable and unique'],
        ['Category', 'Recommended', 'Groups items for filters and reports'],
        ['SKU', 'Optional', 'Internal code for barcodes or supplier cross-ref'],
        ['Unit', 'Yes', 'Shelf count unit (kg, L, pcs, etc.)'],
        ['Minimum stock / reorder', 'Recommended', 'Drives low-stock alerting logic'],
        ['Active', 'Yes', 'Inactive items should not be used on new documents'],
      ]),
      bullets([
        'Search box on the product list',
        'Add / Edit / View drawers',
        'Active / inactive controls',
      ]),
    ],
    workflow: [
      workflow('New item', [
        'Define shelf unit',
        'Create product',
        'First Stock In',
        'Confirm Current Stock',
        'Set reorder habit',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Purchases and movements reference catalogue item IDs. Renaming an item later can confuse staff who memorised the old label — prefer clear names from day one.',
        'Low Stock reports compare current quantity against reorder thresholds configured on the item. Thresholds left at defaults may not match your real par levels — tune them after the first month of operation.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Name for the shelf',
        'Use the name store staff shout (“Hand Towel White”, “Basmati Rice 25kg bag as kg”). Avoid cryptic finance codes as the only name.'
      ),
      callout(
        'tip',
        'Seasonal items',
        'Deactivate festival-only SKUs after the season instead of deleting them.'
      ),
      callout(
        'warning',
        'Unit changes',
        'Changing unit after history exists creates messy ledgers. Create a new item and deactivate the old one if the count unit truly changes.'
      ),
    ],
    bestPractices: [
      bullets([
        'One catalogue row per real SKU — no duplicates with spelling variants.',
        'Align category names with how managers review costs (Amenities, Cleaning, Dairy…).',
        'Review inactive items quarterly so cashiers are not picking obsolete names.',
        'Document unit conversion (e.g. 1 case = 24 bottles) in a store SOP next to the screen.',
        'Set reorder thresholds after watching two weeks of consumption, then refine.',
        'Give kitchen and HK a printed starter list of official item names.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating “Sugar1” and “sugar” duplicates.',
        'Using supplier billing units that nobody counts on the shelf.',
        'Leaving reorder thresholds untouched so Low Stock never fires usefully.',
        'Deleting items with history instead of deactivating.',
        'Putting brand marketing names that change every invoice.',
        'Skipping search and adding the same amenity twice in one afternoon.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Item missing in Stock In',
            'Not created or inactive',
            'Create/activate in Products',
          ],
          [
            'Cannot find after save',
            'Wrong search spelling',
            'Clear filters; search partial name',
          ],
          [
            'Low Stock never shows item',
            'Threshold too low or qty high',
            'Adjust minimum/reorder; recount',
          ],
          [
            'Wrong unit on reports',
            'Catalogue unit mismatch',
            'Stop using old item; create corrected SKU',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I delete items?',
          a: 'Prefer deactivate if any purchase or movement history exists. Deletion can break audit trails.',
        },
        {
          q: 'Do I need a SKU?',
          a: 'Optional. Useful if you match supplier codes or use internal barcodes; otherwise a clear name is enough.',
        },
        {
          q: 'Hotel and restaurant — same product twice?',
          a: 'Usually one catalogue item, tracked per department/pool on documents and reports — not two duplicate product names.',
        },
        {
          q: 'Who should create products?',
          a: 'A store supervisor or manager. Unlimited free-form adds by every shift creates duplicates.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-in',
    title: 'Stock In / Purchases',
    moduleId: 'stock',
    summary:
      'Receive goods into stock, including AI-assisted invoice extraction.',
    keywords: [
      'stock in',
      'purchase',
      'add inventory',
      'invoice extract',
      'goods receipt',
    ],
    pathHint: 'Stock → Add Stock',
    related: ['stock-suppliers', 'stock-items', 'stock-movement', 'stock-reports'],
    overview: [
      ...paragraphs(
        'Stock In (Add Stock) records goods receipts that increase on-hand quantity and inventory value. Every delivery you care about for store control should become a Stock In document the same day it arrives.',
        'The form captures supplier (or other purchase-from context), purchase-for department (hotel or restaurant), invoice date/number, GST details, line items with quantities and rates, tax lines (CGST/SGST/IGST), payment status, and optional invoice document uploads.',
        'AI invoice extract can read a photo or PDF of a supplier invoice and prefill lines. It is a speed tool — not a replacement for counting cartons and checking rates. Always verify before save.',
        'You can attach invoice images or PDFs (including camera capture) so accounts and auditors can open the source document later without hunting WhatsApp chats.',
        'Saved receipts feed Current Stock, Valuation, Movement history, and month-end store packs. Unposted deliveries create “mystery stock” on shelves and missing value in reports.'
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Supplier exists and is active under Stock → Suppliers.',
        'All line items exist as Products / Raw materials.',
        'Physical count of the delivery is done (or in progress beside the screen).',
        'Invoice copy (paper, PDF, or photo) is ready.',
        'Know whether goods are for hotel or restaurant pool.',
      ]),
    ],
    steps: [
      heading('Post a goods receipt'),
      steps([
        { text: 'Open Stock → Add Stock / Stock In.' },
        {
          text: 'Set purchase-for (hotel or restaurant) and select the supplier.',
        },
        {
          text: 'Enter invoice date and invoice number; add GST number if applicable.',
        },
        {
          text: 'Optional: open AI invoice extract, upload/capture the invoice, apply results, then verify every line.',
        },
        {
          text: 'Add or correct item rows: product, quantity, rate, and tax (use GST slab helpers where provided).',
        },
        {
          text: 'Record payment type (paid / unpaid) and payment method rows if paid.',
        },
        {
          text: 'Attach invoice documents, review billing totals, and Save. Confirm on-hand increased.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Purchase for', 'Yes', 'Hotel or restaurant pool receiving the goods'],
        ['Supplier', 'Yes (typical)', 'Vendor from Suppliers master'],
        ['Invoice date / number', 'Yes', 'Audit reference matching supplier bill'],
        ['GST number', 'As required', 'Tax identity on the purchase'],
        ['Line item / qty / rate', 'Yes', 'What arrived and at what cost'],
        ['CGST / SGST / IGST', 'As required', 'Tax split on lines or via slab actions'],
        ['Payment type / methods', 'Yes', 'Paid vs unpaid; cash/UPI/card style methods'],
        ['Invoice documents', 'Recommended', 'Photo/PDF attachments for audit'],
      ]),
      bullets([
        'AI extract overlay',
        'Line item grid with qty steppers',
        'Billing summary totals',
        'Camera / file upload for invoices',
      ]),
    ],
    workflow: [
      workflow('Goods receipt', [
        'Delivery arrives',
        'Count vs invoice',
        'Stock In entry',
        'Attach invoice',
        'Save',
        'File for accounts',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Saving Stock In increases on-hand quantity for the selected department/pool and updates valuation using the costs you entered. Split payments and unpaid purchases are stored on the document for later follow-up.',
        'AI extract attempts to match supplier names and line items to existing masters. Close-but-wrong matches are common — treat suggestions as drafts.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Verify AI lines',
        'Extractors misread quantities, decimals, and rates. Human check against the physical count is mandatory before save.'
      ),
      callout(
        'tip',
        'Same-day posting',
        'Post while the delivery person is still on site when possible. Partial deliveries should match what actually entered the store.'
      ),
      callout(
        'tip',
        'GST slabs',
        'Use the provided GST slab actions for selected rows when purchases are intra-state CGST+SGST; still confirm the invoice tax breakup.'
      ),
      callout(
        'note',
        'Perishables',
        'Put batch/expiry notes in remarks or your store log when the invoice alone is not enough for FIFO discipline.'
      ),
    ],
    bestPractices: [
      bullets([
        'Count first, type second — never trust the invoice alone for open bags or broken seals.',
        'Attach the invoice PDF/photo on every material purchase.',
        'Keep unpaid purchases visible to accounts until supplier payment is cleared in your finance process.',
        'Use consistent invoice numbers so duplicate entry is easy to spot.',
        'Train one “primary poster” per shift to reduce duplicate Stock Ins.',
        'After save, spot-check one high-value line on Current Stock / item ledger.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Entering billed cases as pieces without converting to catalogue units.',
        'Selecting hotel pool when goods went straight to the kitchen (or reverse).',
        'Saving AI extract without fixing wrong supplier or rates.',
        'Forgetting invoice attachments that accounts will demand later.',
        'Posting the full invoice when only part of the order was delivered.',
        'Creating a new product mid-receipt with a duplicate name under pressure.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'AI extract failed',
            'Blurry photo / unsupported layout',
            'Retake clearer image or enter manually',
          ],
          [
            'Wrong totals',
            'Rate/qty/tax mismatch',
            'Fix lines before save; match invoice',
          ],
          [
            'Cannot select supplier',
            'Supplier missing/inactive',
            'Create/activate under Suppliers',
          ],
          [
            'Item not in dropdown',
            'Product missing/inactive',
            'Add product first, then return',
          ],
          [
            'Need to correct after save',
            'Edit restricted by SOP',
            'Use adjustment/movement corrections per property SOP',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I edit Stock In after save?',
          a: 'Follow your property SOP. If edit is restricted, correct quantities with adjustment or a controlled reversal process — do not silently ignore errors.',
        },
        {
          q: 'Must payment be marked paid?',
          a: 'No. Use unpaid when credit purchases arrive; still post the goods so stock is correct.',
        },
        {
          q: 'Is this a formal purchase order module?',
          a: 'Purchases are captured as Stock In receipts with supplier and invoice references. Use those fields as your PO/invoice trail.',
        },
        {
          q: 'Can one invoice split across hotel and restaurant?',
          a: 'Post separate Stock In documents per purchase-for pool when goods are clearly split, matching how they were physically put away.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-movement',
    title: 'Stock Movement',
    moduleId: 'stock',
    summary:
      'Stock out, transfer, adjustment, and item ledger for consumption control.',
    keywords: [
      'stock out',
      'transfer',
      'adjustment',
      'consumption',
      'ledger',
      'issue',
    ],
    pathHint: 'Stock → Stock Movement',
    related: ['stock-in', 'stock-reports', 'stock-dashboard'],
    overview: [
      ...paragraphs(
        'Stock Movement explains every on-hand change that is not a purchase. Use it to issue goods to kitchen or floors (Stock Out), move quantity between hotel and restaurant pools (Transfer), or correct counts after audit (Adjustment).',
        'Without movements, Stock In alone makes the system think you still hold everything you ever bought. That is the most common cause of “system stock high, shelf empty”.',
        'The movement workspace typically shows current lines you can act on, with quantity entry and actions for issue out, transfer, and adjust. You can open an item ledger drawer to see the activity history for a single SKU.',
        'Waste, breakage, and complimentary issues should be posted with clear reasons so owners see shrinkage instead of mysterious shortages at month end.',
        'Large adjustments deserve dual control: one person counts, another posts, and a manager reviews the Adjustment report weekly.'
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Know which pool holds the stock you are issuing from.',
        'Have a reason code habit for waste vs normal issue.',
        'Confirm on-hand is sufficient before attempting a large Stock Out.',
      ]),
    ],
    steps: [
      heading('Issue, transfer, or adjust'),
      steps([
        { text: 'Open Stock → Stock Movement.' },
        {
          text: 'Find the item row (search if needed) and confirm current quantity and unit.',
        },
        {
          text: 'Choose the action: Stock Out (issue), Transfer (between pools/locations), or Adjustment (count correction).',
        },
        {
          text: 'Enter quantity carefully; for transfers set destination context; for adjustments enter the corrected picture and reason.',
        },
        {
          text: 'Save the action and verify on-hand changed as expected.',
        },
        {
          text: 'Open the item ledger (history) when you need to audit who posted what and when.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Item / current qty', '—', 'Shows what is available to issue'],
        ['Stock Out qty', 'When issuing', 'Quantity leaving the store to consumption'],
        ['Transfer qty / destination', 'When transferring', 'Moves between hotel and restaurant pools'],
        ['Adjustment qty / reason', 'When adjusting', 'Corrects book vs physical; reason is mandatory in practice'],
        ['Item ledger', 'Audit', 'Chronological activity for one SKU'],
      ]),
      bullets([
        'Movement worksheet list',
        'Out / Transfer / Adjust actions',
        'Ledger drawer for drill-down',
        'Related adjustment and transfer history sections where shown',
      ]),
    ],
    workflow: [
      workflow('Kitchen issue', [
        'Indent from kitchen',
        'Stock Out posted',
        'Kitchen receives goods',
        'Consumption visible on reports',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Stock Out decreases source on-hand and appears in consumption-oriented reports. Transfers decrease one pool and increase another without counting as a fresh purchase. Adjustments can increase or decrease quantity to match physical count.',
        'Insufficient stock errors mean the book quantity is lower than the qty you tried to issue — either reduce the issue, post missing Stock In, or investigate prior unposted outs.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Waste visibility',
        'Post waste/breakage as a clear adjustment or out reason so owners can see shrinkage separately from normal kitchen use.'
      ),
      callout(
        'warning',
        'Do not “fix” theft with adjustments',
        'Investigate repeated negative variances. Adjustments are for honest count differences, not for hiding loss.'
      ),
      callout(
        'tip',
        'Ledger first',
        'When a chef disputes stock, open the item ledger together and walk the last In/Out/Adjust lines.'
      ),
      callout(
        'note',
        'Transfers',
        'If restaurant borrows from hotel store, post Transfer — do not Stock Out from hotel and Stock In again as a fake purchase.'
      ),
    ],
    bestPractices: [
      bullets([
        'Require dual control on large adjustments.',
        'Issue against written indents during busy banquet days.',
        'Review Adjustment and Transfer reports every week.',
        'Never leave the store open for self-service without posting.',
        'Reconcile high-value items (oil, liquor, linen chemicals) more often than low-value ones.',
        'Train night auditors to spot unusual late-night adjustments.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Transferring without confirming the receiving pool needed the goods.',
        'Issuing more than book quantity and forcing random adjustments afterward.',
        'Using Stock Out when you meant Transfer between hotel and restaurant.',
        'Blank or joke reasons on adjustments (“xyz”, “temp”).',
        'Posting kitchen consumption once a month in a single lump (loses daily control).',
        'Ignoring ledger history when staff argue about “missing” stock.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Insufficient stock',
            'Book qty too low',
            'Reduce qty, post missing Stock In, or recount',
          ],
          [
            'Wrong pool reduced',
            'Incorrect source selection',
            'Correct with transfer/adjustment per SOP; retrain',
          ],
          [
            'Ledger empty',
            'No activity yet',
            'Confirm item id; post a test movement on training hotel',
          ],
          [
            'Cannot post out',
            'Permission or inactive item',
            'Check role; activate product',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is waste a separate module?',
          a: 'Record waste through movement/adjustment reasons and review it on Adjustment or Consumption reports — there is not a standalone waste app screen.',
        },
        {
          q: 'What is the item ledger?',
          a: 'A per-SKU activity history (ins, outs, transfers, adjustments) used for disputes and audits.',
        },
        {
          q: 'Can I reverse a wrong Stock Out?',
          a: 'Follow property SOP — typically a correcting adjustment or controlled reverse entry, with manager approval.',
        },
        {
          q: 'Do POS orders auto Stock Out?',
          a: 'Not automatically assumed. Post consumption explicitly unless your property has a separate agreed process.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-suppliers',
    title: 'Suppliers',
    moduleId: 'stock',
    summary: 'Maintain supplier master data for purchases.',
    keywords: ['suppliers', 'vendors', 'purchase vendors', 'GST', 'FSSAI'],
    pathHint: 'Stock → Suppliers',
    related: ['stock-in', 'stock-items'],
    overview: [
      ...paragraphs(
        'Suppliers are the vendor master for goods you buy into Stock. Create a supplier before posting Stock In so purchases have a clean audit trail and AI invoice extract can match names more reliably.',
        'Each supplier record holds name, phone (with country code), email, address, country/state/city, GST number, FSSAI license number (for food vendors), type (purchase / sell / both), and active status.',
        'Suppliers are not guest customers. Do not create kitchen vendors in Customer Hub, and do not create guests under Stock Suppliers.',
        'Duplicate supplier spellings (“ABC Traders” vs “ABC Trading”) split purchase history and confuse cashiers. Search before adding.',
        'Keep GST and FSSAI fields current for food and tax-sensitive vendors so accounts does not chase the store for missing paperwork at month end.'
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Have legal name, phone, and GST/FSSAI from the vendor’s invoice letterhead.',
        'Know whether the vendor is purchase-only or both purchase/sell if you use that type.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Stock → Suppliers.' },
        { text: 'Search to ensure the vendor does not already exist.' },
        {
          text: 'Click Add and enter name, type, phone with country code, and email if available.',
        },
        {
          text: 'Fill address, country, state, city, GST number, and FSSAI license number as applicable.',
        },
        { text: 'Save with Active enabled.' },
        {
          text: 'Use the supplier on the next Stock In and confirm it appears on purchase documents.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Name', 'Yes', 'Legal/trade name used on invoices'],
        ['Type', 'Yes', 'Purchase, Sell, or Both'],
        ['Phone + country code', 'Recommended', 'Ordering and delivery contact'],
        ['Email', 'Optional', 'For order copies and accounts queries'],
        ['Address / City / State / Country', 'Recommended', 'Delivery and compliance'],
        ['GST number', 'As required', 'Tax identity'],
        ['FSSAI license number', 'Food vendors', 'Food safety compliance reference'],
        ['Active', 'Yes', 'Inactive suppliers should not be selected on new purchases'],
      ]),
    ],
    workflow: [
      workflow('Add vendor', [
        'Collect invoice letterhead details',
        'Create supplier',
        'First Stock In',
        'Accounts validates GST/FSSAI',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Supplier records organise purchase history and help AI extract pick the right vendor. They do not post stock by themselves — Stock In does.',
        'Older internal notes that say “Finance → Vendors” for goods should be read as Stock → Suppliers in the current app.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Finance “vendors”',
        'Goods vendors live under Stock → Suppliers. Guest profiles live under Customer Hub.'
      ),
      callout(
        'tip',
        'Match invoice name',
        'Use the name printed on GST invoices so extract matching and accounts reconciliation stay easy.'
      ),
      callout(
        'tip',
        'Food vendors',
        'Capture FSSAI on dairy, meat, and prepared food suppliers used by the restaurant.'
      ),
    ],
    bestPractices: [
      bullets([
        'One supplier record per legal vendor — merge process via SOP if duplicates appear.',
        'Update phone numbers when the sales rep changes.',
        'Deactivate vendors you no longer use instead of leaving confusing choices.',
        'Store GSTIN exactly as on the registration certificate.',
        'Review supplier list quarterly with accounts.',
        'Prefer official email domains over personal inboxes when possible.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Duplicate suppliers with spelling variants.',
        'Saving without phone, then failing to reach the vendor during stock-outs.',
        'Putting guest names into Suppliers by mistake.',
        'Ignoring GST/FSSAI until an audit week.',
        'Selecting an inactive or wrong similarly named vendor on Stock In.',
        'Creating a new supplier for every branch of the same company without need.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot select supplier on Stock In',
            'Missing or inactive',
            'Create/activate supplier first',
          ],
          [
            'Phone validation error',
            'Wrong country code / length',
            'Fix dial code and national number length',
          ],
          [
            'AI picked wrong supplier',
            'Similar names',
            'Correct manually; rename for uniqueness',
          ],
          [
            'Duplicate found later',
            'Rushed add',
            'Stop using duplicate; keep one master going forward',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where are purchase orders?',
          a: 'Purchases are captured via Stock In receipts. Use supplier + invoice number/date on those documents as your order trail.',
        },
        {
          q: 'Can a supplier be both purchase and sell?',
          a: 'Yes — set Type to Both when your property uses that classification.',
        },
        {
          q: 'Is FSSAI mandatory in the form?',
          a: 'Capture it for food suppliers as part of good compliance practice even when the field is optional in the UI.',
        },
        {
          q: 'Difference from Customer Hub?',
          a: 'Customers are guests. Suppliers are vendors you pay for goods.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'stock-reports',
    title: 'Stock Reports',
    moduleId: 'stock',
    summary:
      'Summary, on-hand, movement, consumption, adjustment, transfer, low stock, and valuation reports.',
    keywords: [
      'stock reports',
      'low stock',
      'valuation',
      'consumption report',
      'stock summary',
    ],
    pathHint: 'Stock → Reports',
    related: ['stock-dashboard', 'reports-hub', 'stock-movement'],
    overview: [
      ...paragraphs(
        'Stock Reports turn posted stock documents into registers managers and accountants can act on — without rebuilding spreadsheets by hand.',
        'Available report types include Stock Summary, Current Stock, Stock Movement, Consumption, Adjustment, Transfer, Low Stock, and Inventory Valuation. Some are date-ranged; others show current on-hand snapshots.',
        'Filters cover item search, department (hotel/restaurant), category, status, transaction type, supplier, locations, and more depending on the report. Many reports open an item History ledger for drill-down.',
        'Export and print tools support PDF, Excel, CSV, and print for owner packs and audits. Always set the correct date range before exporting.',
        'Finance → Reports Hub is separate: that hub covers bookings, payments, GST, police log, and related operational exports. Use Stock → Reports for consumable goods.'
      ),
      table(
        ['Report', 'What it answers'],
        [
          ['Stock Summary', 'Opening / in / out / adjustments / closing for a period'],
          ['Current Stock', 'What is on hand now, value, reorder, status'],
          ['Stock Movement', 'Document-level in/out activity with user and reference'],
          ['Consumption', 'What was consumed and at what value'],
          ['Adjustment', 'Count corrections with reasons'],
          ['Transfer', 'Pool/location moves'],
          ['Low Stock', 'Items at/below reorder with alert highlights'],
          ['Valuation', 'Inventory value by item using average cost'],
        ]
      ),
    ],
    beforeYouBegin: [
      stockPerm,
      bullets([
        'Know whether you need a period report or a current snapshot.',
        'Agree month-end cut-off with accounts before exporting valuation.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Stock → Reports.' },
        {
          text: 'Choose the report type from the report menu (e.g. Low Stock, Valuation).',
        },
        {
          text: 'Set date range when the report is dated (summary, movement, consumption, adjustment, transfer).',
        },
        {
          text: 'Apply filters (department, category, item, status, supplier, etc.) as needed.',
        },
        {
          text: 'Review rows; open History on drill-down reports when investigating a SKU.',
        },
        {
          text: 'Export PDF/Excel/CSV or print for your store meeting or accounts pack.',
        },
      ]),
    ],
    screens: [
      bullets([
        'DashboardReportHeader with date range and export actions',
        'Report type menu',
        'Column filters on the grid',
        'Total inventory value / analytics chips when returned',
        'Item ledger drawer from History',
      ]),
      fieldTable([
        ['Report type', 'Yes', 'Selects which register to load'],
        ['Start / End date', 'When dated', 'Period for movement-style reports'],
        ['Department filter', 'Optional', 'Hotel vs Restaurant'],
        ['Category / item / status', 'Optional', 'Narrow the grid'],
        ['Export actions', 'Optional', 'PDF, Excel, CSV, Print'],
      ]),
    ],
    workflow: [
      workflow('Month-end store', [
        'Freeze late postings',
        'Run Valuation',
        'Run Consumption',
        'Explain adjustments',
        'File with accounts',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Reports read posted stock documents only. Unposted paper invoices and unrecorded issues will not appear.',
        'Low Stock uses reorder thresholds from the product catalogue. Valuation uses average cost and current quantities. Status labels such as Normal, Low Stock, and Out Of Stock help prioritise action.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Start the week with Low Stock',
        'Every Monday, export Low Stock before calling suppliers.'
      ),
      callout(
        'tip',
        'History button',
        'On drill-down reports, use History to open the item ledger instead of guessing why a balance changed.'
      ),
      callout(
        'note',
        'Date range discipline',
        'Compare like periods (1st–month end). Wrong ranges are the #1 cause of “empty” or “missing” rows.',
      ),
      callout(
        'important',
        'Not Finance GST',
        'Stock valuation is goods value. Tax invoice registers for lodging/F&B billing are under Finance → Reports → GST.'
      ),
    ],
    bestPractices: [
      bullets([
        'Reconcile Valuation to purchase invoices monthly with accounts.',
        'Archive exported Low Stock lists after orders are placed.',
        'Review Adjustment report for unusual negative patterns.',
        'Filter by department when hotel and restaurant report separately to owners.',
        'Run Consumption after banquets to brief F&B on usage.',
        'Password-protect emailed exports that include supplier or cost detail if required by policy.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Wrong date range hiding movements.',
        'Exporting Current Stock when you meant period Summary.',
        'Ignoring department filters and mixing pools in one owner total.',
        'Acting on stale exports instead of refreshing after late Stock Ins.',
        'Confusing Stock Reports with Finance Reports Hub.',
        'Never opening History when a variance is disputed.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Empty report',
            'No postings / bad dates / filters',
            'Widen dates; clear filters; confirm documents exist',
          ],
          [
            'Valuation looks low',
            'Missing Stock Ins or wrong costs',
            'Audit recent purchases; fix costs via SOP',
          ],
          [
            'Low Stock empty but shelves bare',
            'Reorder thresholds too low',
            'Raise minimum/reorder on products',
          ],
          [
            'Export disabled',
            'No rows loaded',
            'Generate report with data first',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Different from Finance Reports?',
          a: 'Yes. Stock Reports are goods. Finance Reports Hub covers bookings, payments, GST invoice register, police log, customers, room inventory snapshots, and restaurant orders.',
        },
        {
          q: 'Which report for reordering?',
          a: 'Low Stock first; optionally Current Stock for full on-hand with status.',
        },
        {
          q: 'Can I see who posted a movement?',
          a: 'Stock Movement and Adjustment reports include user fields; item History/ledger shows activity detail.',
        },
        {
          q: 'Are reports live?',
          a: 'They refresh from posted data when you load/refresh. Post first, then report.',
        },
      ]),
    ],
  }),
];

export const roomInventoryGuides = [
  buildGuide({
    id: 'room-inventory-overview',
    title: 'Room Inventory Overview',
    moduleId: 'room-inventory',
    summary:
      'Manage sellable room availability by date, type, and channel-aware allotments.',
    keywords: [
      'room inventory',
      'availability',
      'allotment',
      'manage inventory',
      'overbooking',
    ],
    pathHint: 'Inventory / Manage Inventory',
    related: [
      'inventory-dashboard',
      'manage-inventory',
      'inventory-list',
      'channel-inventory',
    ],
    overview: [
      ...paragraphs(
        'Room Inventory controls how many rooms of each type are available to sell on each date. It is the availability backbone behind front-office bookings and, when distribution is live, channel allotments.',
        'This is not Stock Management. Stock tracks soap and rice; Room Inventory tracks Deluxe and Suite nights. Training should use the full names until new staff stop mixing them up.',
        'Day-to-day work happens on Inventory Dashboard (health), Inventory List (tabular review), and Manage Inventory (daily grid edits, blocks, channel-aware cells). Distribution tools extend allotments to OTAs.',
        'Bookings consume availability. Manual inventory cuts, event blocks, and out-of-order decisions further constrain what can sell. Cutting inventory below already-committed stays creates stop-sell pressure and overbook risk.',
        'After large inventory changes — renovations, wedding holds, festival stop-sells — verify front desk, sales, and Channel Inventory / mapping so OTAs do not keep selling closed dates.'
      ),
      callout(
        'important',
        'Channels',
        'When OTAs are connected, inventory edits and allotments must stay aligned. Confirm Distribution / Channel Inventory after major changes.'
      ),
    ],
    beforeYouBegin: [
      roomInvPerm,
      bullets([
        'Room types and physical rooms must already exist in Configuration.',
        'Know which channels are live before changing allotments.',
        'Agree who may edit Manage Inventory (usually revenue/FO manager).',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Review Inventory Dashboard for tight dates and occupancy pressure.',
        },
        {
          text: 'Use Inventory List to inspect date rows and open date detail when needed.',
        },
        {
          text: 'Edit availability on Manage Inventory for the date range and room types affected.',
        },
        {
          text: 'Apply inventory blocks for renovations/events when that workflow is used.',
        },
        {
          text: 'If channels are live, verify Channel Inventory / distribution push implications.',
        },
        {
          text: 'Brief front office so desk staff know which dates are restricted.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Inventory Dashboard — availability/occupancy-oriented KPIs',
        'Inventory List — filters, table, date detail drawer',
        'Manage Inventory — daily grid, channel-aware cells, blocks/adjust drawers',
      ]),
      table(
        ['Situation', 'Typical action'],
        [
          ['Renovation wing closed', 'Reduce inventory / block dates; inform FO'],
          ['City festival demand', 'Protect allotment; raise rates via Manage Rate / Dynamic Pricing'],
          ['Overbook risk', 'Cut OTA allotment; review double bookings'],
        ]
      ),
    ],
    workflow: [
      workflow('Stop sales for renovation', [
        'Decide dates & room types',
        'Reduce inventory / create block',
        'Inform front office',
        'Verify channels',
        'Monitor Dashboard',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Sellable inventory is capacity minus bookings and blocks/constraints you apply. Lowering a cell below committed stays does not erase existing reservations — it reduces what else can sell and can create operational overbook risk.',
        'Channel allotments decide how much of your PMS availability each OTA may sell. Silent over-allocation to multiple channels is a classic overbook cause.'
      ),
    ],
    tips: [
      callout(
        'important',
        'Channels after edits',
        'After big inventory changes, verify Channel Inventory / mapping and that stop-sell intent reached OTAs.'
      ),
      callout(
        'tip',
        'Rates vs inventory',
        'For high demand, prefer sensible rate increases (Manage Rate / Dynamic Pricing) over blindly zeroing inventory — unless you truly cannot host more guests.'
      ),
      callout(
        'warning',
        'Tell the desk',
        'Inventory cuts without briefing front office cause awkward walk situations and angry sales calls.'
      ),
    ],
    bestPractices: [
      bullets([
        'Document why inventory was changed (renovation, VIP hold, event).',
        'Never silently over-allocate the same rooms to multiple OTAs.',
        'Review next 14 days daily in peak season on Dashboard + Manage Inventory.',
        'Use two-person check for mass grid edits.',
        'Align inventory blocks with housekeeping OOO/maintenance reality.',
        'Re-open inventory promptly when renovations finish.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Cutting inventory without telling front desk or sales.',
        'Confusing Stock goods screens with Room Inventory.',
        'Lowering allotment below already booked rooms “to be safe”.',
        'Editing one channel’s view and assuming all channels updated.',
        'Leaving festival closures in place after the event ends.',
        'Fixing overbook only on OTAs while PMS grid still looks open.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Overbook risk',
            'Allotments + bookings exceed capacity',
            'Reduce OTA allotment; check booking list; relocate if needed',
          ],
          [
            'Date shows zero but rooms empty',
            'Block or manual cut left in place',
            'Review Manage Inventory / blocks; restore carefully',
          ],
          [
            'Channels still selling',
            'Distribution not updated',
            'Open Distribution inventory tools; confirm mapping',
          ],
          [
            'Module missing',
            'Permission/package',
            'Ask admin for manage_inventory',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Same as Stock?',
          a: 'No — Room Inventory is rooms/nights; Stock is consumables.',
        },
        {
          q: 'Who should edit Manage Inventory?',
          a: 'Usually revenue manager or FO manager. Limit mass-edit rights.',
        },
        {
          q: 'Do bookings auto-reduce availability?',
          a: 'Yes — bookings consume sellable inventory. Manual edits add further constraints.',
        },
        {
          q: 'Where do I stop OTA sales only?',
          a: 'Use channel allotment / Distribution inventory controls rather than blindly zeroing all PMS availability if walk-ins should continue.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'inventory-dashboard',
    title: 'Inventory Dashboard (Rooms)',
    moduleId: 'room-inventory',
    summary:
      'Room inventory insights and occupancy-oriented inventory KPIs.',
    keywords: [
      'inventory dashboard',
      'room inventory stats',
      'availability kpi',
    ],
    pathHint: 'Inventory Dashboard',
    related: ['manage-inventory', 'dashboard-home', 'inventory-list'],
    overview: [
      ...paragraphs(
        'Inventory Dashboard visualises room inventory posture for managers — how availability and booking pressure look across the selected date range.',
        'Use it to spot tight dates early (city events, long weekends, wedding season) before the desk discovers sold-out surprises at check-in time.',
        'It complements the hotel Performance Dashboard: Inventory focuses on availability structure and booking load against capacity; Performance focuses on broader revenue KPIs.',
        'When widgets look odd (zeros, flat lines), confirm room types are configured and inventory slots exist for the range, then jump to Manage Inventory to act.'
      ),
    ],
    beforeYouBegin: [
      roomInvPerm,
      bullets([
        'Know the date range you care about (next 7 / 14 / 30 days).',
        'Have Manage Inventory ready for corrective edits.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Inventory Dashboard.' },
        {
          text: 'Set the date range for the period you want to inspect.',
        },
        {
          text: 'Read availability / occupancy-oriented widgets and daily trend charts.',
        },
        {
          text: 'Note tight or overbooked-looking dates.',
        },
        {
          text: 'Jump to Manage Inventory or Inventory List to adjust allotments or investigate.',
        },
      ]),
    ],
    screens: [
      bullets([
        'KPI metric cards',
        'Charts / daily inventory trends',
        'Date range controls',
        'PDF download options where provided on the page',
      ]),
      table(
        ['Watch for', 'Next action'],
        [
          ['Near-full dates', 'Protect remaining allotment; review rates'],
          ['Sudden voids', 'Check blocks / OOO / cancelled groups'],
          ['Channel vs direct imbalance', 'Review distribution allotments'],
        ]
      ),
    ],
    workflow: [
      workflow('Weekly inventory review', [
        'Open dashboard',
        'Spot tight dates',
        'Adjust Manage Inventory',
        'Brief FO & sales',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Figures are derived from configured rooms, inventory slots, and bookings in range. Empty configuration produces empty or misleading widgets.',
        'Daily trends help you see within-capacity booking versus pressure that needs allotment or rate action.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Event dates',
        'Check city events and raise rates via Dynamic Pricing / Manage Rate — do not rely only on inventory cuts.'
      ),
      callout(
        'tip',
        '14-day habit',
        'In peak season, review the next 14 days every morning with FO.'
      ),
      callout(
        'note',
        'vs Performance Dashboard',
        'Use both: Inventory for “can we sell?”, Performance for “how are we earning?”.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review next 14 days daily in peak season.',
        'Export/share PDF snapshots for owner meetings when useful.',
        'Pair dashboard review with rate strategy, not inventory alone.',
        'Investigate odd zeros before trusting them for stop-sell decisions.',
        'Align tight-date lists with sales team pickup reports.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Looking only at today and missing next weekend’s sell-out.',
        'Cutting inventory from dashboard panic without checking committed bookings.',
        'Ignoring channel contribution when dates look full.',
        'Treating a chart blip as a system bug instead of a group booking.',
        'Never acting — watching red dates until overbook.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Odd zeros',
            'Rooms/inventory not configured',
            'Confirm room types & inventory setup',
          ],
          [
            'Charts empty',
            'Date range / no data',
            'Widen range; confirm bookings exist',
          ],
          [
            'Disagrees with Manage Inventory',
            'Stale view / recent edits',
            'Refresh; re-open Manage Inventory',
          ],
          [
            'Cannot download',
            'Page still loading / browser block',
            'Wait for load; allow downloads',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'vs Performance Dashboard?',
          a: 'Inventory focuses on availability structure; Performance focuses on broader hotel revenue KPIs. Use both.',
        },
        {
          q: 'Can I edit inventory here?',
          a: 'Dashboard is insight-first. Make controlled edits on Manage Inventory.',
        },
        {
          q: 'Does it include Stock goods?',
          a: 'No — room inventory only. Consumables are under Stock.',
        },
        {
          q: 'How often should managers open it?',
          a: 'Daily in peak; at least several times a week otherwise.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'inventory-list',
    title: 'Inventory List',
    moduleId: 'room-inventory',
    summary:
      'Browse room inventory records and open details for dates/types.',
    keywords: ['inventory list', 'inventory detail', 'availability list'],
    pathHint: 'Inventory List',
    related: ['manage-inventory', 'inventory-dashboard'],
    overview: [
      ...paragraphs(
        'Inventory List is the tabular way to inspect inventory rows by date and related metrics, then drill into a date detail drawer for deeper breakdown.',
        'Use it when you need to find sold-out or high-occupancy dates quickly with filters, or when a manager asks “what does next Saturday look like by type?”',
        'For bulk date edits across a range, prefer Manage Inventory’s grid. List is better for investigation and sharing a filtered view with sales.',
        'Booking percentage and related indicators help you see pressure at a glance; open detail before changing strategy on a high-value date.'
      ),
    ],
    beforeYouBegin: [
      roomInvPerm,
      bullets([
        'Know the date range to filter.',
        'Use Manage Inventory when you already know you must edit many cells.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Inventory List.' },
        {
          text: 'Filter by date range and any room-type / status filters available.',
        },
        {
          text: 'Scan booking pressure indicators for tight dates.',
        },
        {
          text: 'Open a row to view date detail.',
        },
        {
          text: 'If correction is needed, move to Manage Inventory and edit carefully.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Filters (dates and related selectors)',
        'Data table of inventory rows',
        'Booking percentage / pressure visuals',
        'Inventory date detail drawer',
      ]),
      fieldTable([
        ['Date filters', 'Recommended', 'Narrow to the period under review'],
        ['Room type filter', 'Optional', 'Focus one category'],
        ['Row → detail', '—', 'Opens date-level breakdown'],
      ]),
    ],
    workflow: [
      workflow('Investigate shortage', [
        'Filter sold-out / tight dates',
        'Open detail',
        'Check bookings',
        'Adjust Manage Inventory or move bookings',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'The list reflects stored inventory availability records for the hotel. Empty lists usually mean inventory was not generated/managed for that range yet.',
        'Detail drawers summarise that date’s inventory picture to support decisions — they do not replace booking list investigation for individual guests.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Prefer Manage Inventory for bulk edits',
        'Use the list to find problems; use the grid to fix ranges efficiently.'
      ),
      callout(
        'tip',
        'Share tight dates',
        'Export or screenshot filtered tight dates for the sales huddle.'
      ),
      callout(
        'tip',
        'Detail before panic',
        'Open date detail before zeroing inventory — you may only need a channel allotment cut.'
      ),
    ],
    bestPractices: [
      bullets([
        'Export/share tight dates with sales weekly.',
        'Check existing bookings before any downward edit.',
        'Keep list filters standardised (e.g. rolling next 30 days).',
        'Escalate true overbooks immediately to FO manager.',
        'After edits, refresh the list to confirm the new picture.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Editing blindly without checking existing bookings.',
        'Using stale filtered views from yesterday.',
        'Trying to do mass holiday edits only from the list.',
        'Ignoring detail drawer charts/summaries.',
        'Confusing list percentages with final house occupancy for today.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Empty list',
            'No inventory for range',
            'Generate/manage inventory for the dates',
          ],
          [
            'Cannot open detail',
            'UI/network glitch',
            'Refresh; retry; check console only if IT supports',
          ],
          [
            'Numbers look wrong',
            'Filters hiding rows',
            'Reset filters; verify hotel context',
          ],
          [
            'Need to edit inline',
            'List is view-oriented',
            'Use Manage Inventory grid',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I edit inline?',
          a: 'Use Manage Inventory for controlled grid edits. List is primarily browse + detail.',
        },
        {
          q: 'Is this Stock list?',
          a: 'No — this is room inventory dates/types.',
        },
        {
          q: 'Why use list instead of dashboard?',
          a: 'List is better for filtered date hunting and opening per-date detail.',
        },
        {
          q: 'Does detail show guest names?',
          a: 'Use Booking List for guest-level investigation; inventory detail focuses on availability structure.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'manage-inventory',
    title: 'Manage Inventory (Daily Grid)',
    moduleId: 'room-inventory',
    summary:
      'Edit day/channel-oriented inventory in a grid for room types.',
    keywords: [
      'manage inventory',
      'daily inventory',
      'inventory grid',
      'allotment',
      'inventory block',
    ],
    pathHint: 'Manage Inventory',
    related: ['channel-inventory', 'inventory-blocks', 'inventory-dashboard'],
    overview: [
      ...paragraphs(
        'Manage Inventory is the operational grid for changing sellable availability across dates and room types. It is the critical screen before holidays, renovations, and large group holds.',
        'The grid shows room-type rows against dates. Depending on your setup, cells can be channel-aware. Clicking cells can open drawers to adjust inventory, apply blocks, or review options for that date/type.',
        'Save carefully. Bookings already on the books still occupy rooms — lowering inventory below committed stays does not cancel guests; it creates stop-sell pressure and potential overbook pain.',
        'Inventory blocks (event/renovation style) help close or restrict ranges without editing every cell manually. Clear blocks when the event ends.',
        'When OTAs are live, treat Manage Inventory and Distribution allotments as one process. A perfect PMS grid with wrong channel allotments still overbooks.'
      ),
    ],
    beforeYouBegin: [
      roomInvPerm,
      bullets([
        'Check occupancy / Booking List for the dates you will cut.',
        'Know which channels are live and who confirms OTA push.',
        'Have a written reason for mass edits (SOP / chat log).',
      ]),
    ],
    steps: [
      heading('Edit availability safely'),
      steps([
        { text: 'Open Manage Inventory.' },
        {
          text: 'Select the date range and room types you need to work on.',
        },
        {
          text: 'Review current cell values and any channel-aware columns shown.',
        },
        {
          text: 'Edit cells or open the adjust/block drawers for controlled changes.',
        },
        {
          text: 'Save changes and re-scan the range for accidental zeros or typos.',
        },
        {
          text: 'If OTAs are live, confirm channel inventory/allotment implications.',
        },
        {
          text: 'Brief front office and sales on restricted dates.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Date navigation / range controls',
        'Room type rows',
        'Day cells (optionally channel-aware)',
        'Inventory adjust drawers',
        'Inventory / event block tools',
        'Save confirmation',
      ]),
      fieldTable([
        ['Date range', 'Yes', 'Which nights you are editing'],
        ['Room type', 'Yes', 'Which product’s availability changes'],
        ['Cell allotment / availability', 'Yes', 'Sellable count for that date'],
        ['Channel context', 'When shown', 'Per-channel availability/allotment'],
        ['Block / adjust drawers', 'As needed', 'Range restrictions and structured edits'],
      ]),
    ],
    workflow: [
      workflow('Holiday lock', [
        'Select holiday range',
        'Review committed bookings',
        'Set allotments / blocks',
        'Save',
        'Verify OTA',
        'Brief FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Grid saves update sellable counts for future sales. Existing reservations remain. If you set availability below already-booked rooms, you are operationally over-constrained and must manage walk risk.',
        'Blocks and adjustments are part of the same availability story as cell edits. Always confirm the final picture on Dashboard/List after saving.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Do not go below committed bookings',
        'Always check occupancy before cutting allotment aggressively.'
      ),
      callout(
        'tip',
        'Small ranges first',
        'If save fails on a huge range, shrink the date window and retry.'
      ),
      callout(
        'important',
        'Two-person check',
        'For wedding or renovation mass edits, have a second person review before save.'
      ),
      callout(
        'note',
        'Slots',
        'Inventory slots are the underlying date/type availability units represented in these screens.'
      ),
    ],
    bestPractices: [
      bullets([
        'Two-person check for mass edits.',
        'Log the business reason in internal SOP/chat.',
        'Re-open inventory promptly when closures end.',
        'Coordinate with HK OOO so closed rooms are not sold.',
        'After save, spot-check three random dates in the range.',
        'Align rate strategy the same day you protect inventory.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Saving without reviewing the full visible range.',
        'Zeroing all channels when walk-ins should remain open.',
        'Leaving renovation blocks active after work finishes.',
        'Editing while looking at the wrong month.',
        'Assuming OTA stop-sell happened without checking Distribution.',
        'Fixing overbook only in the grid and forgetting guest reassignments.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Save error',
            'Network / oversized range / validation',
            'Refresh; reduce range; retry',
          ],
          [
            'Channels wrong after save',
            'Allotment not updated',
            'Open Distribution inventory tools',
          ],
          [
            'Cell won’t reduce',
            'Committed bookings constraint',
            'Review bookings; relocate or accept limit',
          ],
          [
            'Grid looks empty',
            'No room types / wrong dates',
            'Confirm configuration; navigate date picker',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'What are inventory slots?',
          a: 'Underlying date/type availability units represented in Manage Inventory and related screens.',
        },
        {
          q: 'Should I use blocks or cell edits?',
          a: 'Blocks help for renovations/events across ranges; cell edits fine-tune allotments. Use both as your SOP defines.',
        },
        {
          q: 'Will this cancel bookings?',
          a: 'No. It changes sellable availability. Existing bookings stay until you modify them separately.',
        },
        {
          q: 'Who needs access?',
          a: 'Limit to revenue/FO managers. Broad edit rights increase overbook risk.',
        },
      ]),
    ],
  }),
];
